import type { CodeableConcept, Observation, ObservationReferenceRange } from '@medplum/fhirtypes';

/**
 * Interpretation codes that should flag a row in the results table per the FHIR
 * [observation-interpretation valueset](https://www.hl7.org/fhir/valueset-observation-interpretation.html).
 * The set is broader than just "critical" — it covers any abnormal interpretation a clinician
 * scanning the table should notice.
 *
 * - `AA` — critical abnormal
 * - `HH` — critical high
 * - `LL` — critical low
 * - `A` — abnormal (non-critical)
 * - `H` — high (above normal)
 * - `L` — low (below normal)
 */
const CRITICAL_INTERPRETATION_CODES = new Set(['AA', 'HH', 'LL', 'A', 'H', 'L']);

/** True when the observation's primary interpretation is critical or abnormal. Used to flag rows. */
export function isCriticalObservation(observation: Observation): boolean {
  const code = observation.interpretation?.[0]?.coding?.[0]?.code;
  return code !== undefined && CRITICAL_INTERPRETATION_CODES.has(code);
}

/**
 * True when a quantity reading sits outside the first reference range the lab supplied. A reading with
 * no quantity, or no bounded range, is not out of range — there is nothing to compare.
 */
export function isOutOfReferenceRange(observation: Observation): boolean {
  const value = observation.valueQuantity?.value;
  const range = observation.referenceRange?.[0];
  if (value === undefined || !range) return false;
  const low = range.low?.value;
  const high = range.high?.value;
  return (low !== undefined && value < low) || (high !== undefined && value > high);
}

/**
 * True when a reading should be flagged as abnormal: the lab said so in `interpretation`, or it gave
 * a reference range the value falls outside of. Imports carry either or both, so both are read.
 */
export function isAbnormalObservation(observation: Observation): boolean {
  return isCriticalObservation(observation) || isOutOfReferenceRange(observation);
}

/**
 * Format an `ObservationReferenceRange[]` for display in the results table. Picks the first
 * range and prefers an explicit `text` (e.g. `"<140 mg/dL"`) when the lab provided one;
 * otherwise falls back to a `low–high` rendering using whichever bounds are present. Returns
 * `null` when the range carries no displayable content.
 */
export function formatReferenceRange(ranges: ObservationReferenceRange[] | undefined): string | null {
  const range = ranges?.[0];
  if (!range) return null;
  if (range.text) return range.text;
  const low = range.low?.value;
  const high = range.high?.value;
  if (low !== undefined && high !== undefined) return `${low}–${high}`;
  if (low !== undefined) return `>${low}`;
  if (high !== undefined) return `<${high}`;
  return null;
}

/** Format the primary interpretation for display (e.g. "Normal", "Below low normal"). */
export function formatInterpretation(interpretations: CodeableConcept[] | undefined): string | null {
  const interp = interpretations?.[0];
  if (!interp) return null;
  if (interp.text) return interp.text;
  const coding = interp.coding?.[0];
  return coding?.display ?? coding?.code ?? null;
}
